import { useContext } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  SidebarDateField,
  SidebarSelectField,
} from '@/views/Order/components/details/OrderDetailSidebar';
import { SidebarTextField } from '@/views/Order/components/details/SidebarTextField';
import { formatDateTimeLocalized, formatShortDate } from '@/utils/formatter';
import i18n from '@/utils/i18n';
import {
  CanAccessOperationsData,
  ViewerContext,
} from '@/providers/ViewerContextProvider';
import type { PickingJobDetailQuery } from '@/graphql/queries/pickingJobDetail.generated';
import {
  formatByline,
  pickingJobSegmentLabel,
  pickingJobStatusLabel,
  resolutionDestinationLabel,
} from './pickingJobLabels';
import { PickingJobActions } from './PickingJobActions';
import { destinationLabel } from './pickingJobFilters';

type PickingJobDetail = NonNullable<PickingJobDetailQuery['getPickingJob']>;

/**
 * Right rail of the picking job detail page. Uses the ops order sidebar's own
 * field components so the two pages look identical; every field is read-only
 * because nothing about a picking job is edited here — the action below them
 * is a resolution, not an edit. Free text (the reason, the carrier comment)
 * wraps in full instead of truncating to one line.
 */
export function PickingJobSidebar({
  job,
  onActionSettled,
}: {
  job: PickingJobDetail;
  onActionSettled: () => void;
}) {
  // resolvePickingJob requires mustBeAbleToAccessOperationsData in colugo, so the
  // card only appears for the roles that can actually use it; everyone else
  // who may open the page (support, account managers, event managers) reads
  // the job without buttons that would only ever say FORBIDDEN.
  const viewer = useContext(ViewerContext);
  const canAct = CanAccessOperationsData(viewer);
  const deliveryConfirmed = job.delivery_confirmed_at
    ? formatByline(job.delivery_confirmed_at, job.delivery_confirmed_by)
    : null;
  const { resolution } = job;

  return (
    <div className="tw:sticky tw:top-4 tw:w-[284px] tw:shrink-0 tw:self-start tw:flex tw:flex-col tw:gap-3">
      <Card>
        <CardContent className="tw:flex tw:flex-col tw:gap-4">
          <div className="tw:flex tw:flex-col tw:gap-4">
            <SidebarSelectField
              label={i18n.t('Picking:columnStatus')}
              value={pickingJobStatusLabel(job.status)}
              disabled
            />
            <SidebarSelectField
              label={i18n.t('Picking:columnSegment')}
              value={pickingJobSegmentLabel(job.segment)}
              disabled
            />
            <SidebarSelectField
              label={i18n.t('Picking:columnWarehouse')}
              value={job.item_location_warehouse.name ?? '-'}
              disabled
            />
            <SidebarSelectField
              label={i18n.t('Picking:columnDestination')}
              value={destinationLabel(job)}
              disabled
            />
          </div>

          <Separator />

          <div className="tw:flex tw:flex-col tw:gap-4">
            <SidebarDateField
              label={i18n.t('Picking:columnPickupDate')}
              value={
                job.carrier_pickup_date
                  ? formatShortDate(job.carrier_pickup_date)
                  : '-'
              }
            />
            <SidebarDateField
              label={i18n.t('Picking:detailDeliveryDate')}
              value={
                job.carrier_delivery_date
                  ? formatShortDate(job.carrier_delivery_date)
                  : '-'
              }
            />
            <SidebarSelectField
              label={i18n.t('Picking:detailWeight')}
              value={job.total_weight != null ? `${job.total_weight} kg` : '-'}
              disabled
            />
            <SidebarSelectField
              label={i18n.t('Picking:detailPallets')}
              value={job.pallet_count != null ? String(job.pallet_count) : '-'}
              disabled
            />
            <SidebarSelectField
              label={i18n.t('Picking:detailPackages')}
              value={
                job.package_count != null ? String(job.package_count) : '-'
              }
              disabled
            />
          </div>

          <Separator />

          <div className="tw:flex tw:flex-col tw:gap-4">
            <SidebarDateField
              label={i18n.t('Picking:detailCreatedAt')}
              value={formatDateTimeLocalized(
                new Date(job.created_at),
                i18n.language,
              )}
            />
            <SidebarDateField
              label={i18n.t('Picking:detailUpdatedAt')}
              value={formatDateTimeLocalized(
                new Date(job.updated_at),
                i18n.language,
              )}
            />
            {deliveryConfirmed && (
              <SidebarDateField
                label={i18n.t('Picking:detailDeliveryConfirmed')}
                value={deliveryConfirmed}
              />
            )}
            {job.carrier_employee_comment && (
              <SidebarTextField
                label={i18n.t('Picking:detailCarrierComment')}
                value={job.carrier_employee_comment}
              />
            )}
            {/* The record of the resolution. There is no audit table for
                picking, so these fields are the whole trail — which is why they
                sit next to the ordinary timestamps rather than behind a
                disclosure. */}
            {resolution && (
              <>
                <SidebarSelectField
                  label={i18n.t('Picking:detailResolutionDestination')}
                  value={resolutionDestinationLabel(resolution.destination)}
                  disabled
                />
                <SidebarDateField
                  label={i18n.t('Picking:detailResolvedAt')}
                  value={formatByline(
                    resolution.resolved_at,
                    resolution.resolved_by,
                  )}
                />
                <SidebarTextField
                  label={i18n.t('Picking:detailResolutionReason')}
                  value={resolution.reason}
                />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {canAct && (
        <Card>
          <CardContent className="tw:flex tw:flex-col tw:gap-3">
            <h2 className="tw:text-sm tw:font-semibold">
              {i18n.t('Picking:actionsTitle')}
            </h2>
            <PickingJobActions
              pickingJobId={job.id}
              segment={job.segment}
              status={job.status}
              // Only the orders this job owns: one a newer live job holds is
              // left alone by the resolution and ignored in its gates.
              orderStatuses={job.ops_orders
                .filter((o) => o.owned_by_job)
                .map((o) => o.status)}
              linkedOrderCount={job.ops_orders.length}
              resolved={resolution != null}
              hasBookedOrDeliveredRows={job.has_booked_or_delivered_rows}
              onSettled={onActionSettled}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
